import update from 'react/lib/update';
import _ from 'lodash';

export const CONTENT_ELEMENT_TYPE_PRODUCT = 1;
export const CONTENT_ELEMENT_TYPE_MEDIA = 2;
export const THUMB_IMAGE_KEY = 'image.imageName.sr_60x60';

/**
 * ConfiguratorManager
 *
 * @author Bogdan Habinc <habinc@onlineflex.de>
 */
class ConfiguratorManager {

    /**
     * Constructor
     *
     * @param configuratorState
     * @param documentStorageState
     * @param documentId
     * @param storageId
     * @param pageId
     * @param authorId
     * @param isCopy
     */
    constructor(configuratorState, documentStorageState, documentId, storageId, pageId, authorId,isCopy=0) {
        this.configuratorState = configuratorState;
        this.documentStorageState = documentStorageState;
        this.documentId = documentId;
        this.storageId = storageId;
        this.pageId = pageId;
        this.authorId = authorId;
        this.isCopy = isCopy;
        if (pageId.toString() === '0') {
            this.pageId = this.getFirstPageId();
        }
    }


    getCopyState(){
        return this.isCopy;
    }

    /**
     * Return the current document ID
     *
     * @returns string
     */
    getDocumentId() {
        return this.documentId;
    }

    /**
     * Return the current storage ID
     *
     * @returns string
     */
    getStorageId() {
        return this.storageId;
    }

    /**
     * Return the current page ID
     *
     * @returns string
     */
    getPageId() {
        return this.pageId;
    }

    /**
     * Return the authorId
     *
     * @returns string
     */
    getAuthorId() {
        return this.authorId;
    }

    /**
     * Return the current document
     *
     * @returns {*}
     */
    getDocument() {
        let document = null;
        const documentId = this.documentId;
        if (this.configuratorState.data.length > 0) {
            this.configuratorState.data.map(function (documentEntry) {
                if (documentEntry.id.toString() == documentId.toString()) {
                    document = documentEntry;
                }
            });
        }

        return document;
    }

    _getRawDocumentSpecification()
    {
        const document = this.getDocument();

        let specification = null;
        if (document && document.translationsSorted && document.translationsSorted[`${this.getDocumentStorageLocale()}`]
            && document.translationsSorted[`${this.getDocumentStorageLocale()}`]['specification']) {
            specification = document.translationsSorted[`${this.getDocumentStorageLocale()}`]['specification']
        }

        return specification;
    }

    /**
     * Return the current document specification object
     *
     * @returns {*}
     */
    getDocumentSpecification() {

        let specification = this._getRawDocumentSpecification();

        // On layoutvariants there are multiple document specifications defined (as collection)
        if (specification instanceof Array && specification.length > 0) {

            // get specificationId from documentStorage, if defined
            const storageValues = this.getDocumentStorageValues();

            let specificationId = null;
            let specificationEntry = null;

            if (storageValues && storageValues.documentSpecificationId) {
                specificationId = storageValues.documentSpecificationId;
                specification.map((entry) => {
                    if (entry.id.toString() === specificationId.toString()) {
                        specificationEntry = entry;
                    }
                });
            }

            if (specificationEntry) {
                specification = specificationEntry;
            } else {
                specification = specification[0]; // if no specificationID in storage, return first collection entry
            }
        }

        return specification;
    }

    /**
     * If there are more then one DocumentSpecifications defined
     *
     * @returns {Array}
     */
    getDocumentSpecificationMultipleLabels() {
        let labels = [];
        let specification = this._getRawDocumentSpecification();

        // On layoutvariants there are multiple document specifications defined (as collection)
        // If there are at least two specifications, we handle multiple specifications
        if (specification instanceof Array && specification.length > 1) {

            specification.map((entry) => {

                let label = {
                    id: null,
                    title: null,
                    selected: false,
                };

                label.id = entry.id;
                label.title = entry.title;
                labels.push(label);
            });
        }

        return labels;
    }

    /**
     * Returns the stored documentSpecificationId if exists
     *
     * @returns string | null
     */
    getStoredDocumentSpecificationId() {

        let specificationId = "";
        const storageValues = this.getDocumentStorageValues();

        if (storageValues && storageValues.documentSpecificationId) {
            specificationId = storageValues.documentSpecificationId;
        }

        return specificationId;
    }


    /**
     * Return the pages collection (all pages, from specification and storage)
     *
     * @returns []
     */
    getPages() {

        const specificaton = this.getDocumentSpecification();
        const storage = this.getDocumentStorageValues();

        let pages = [];

        if (storage && storage.pages && storage.pages instanceof Array && storage.pages.length > 0) {
            pages = this._getStoragePages();
        } else if (specificaton && specificaton.pages && specificaton.pages instanceof Array) {
            pages = this.getDocumentPages();
        }

        return pages;
    }

    /**
     * Return all available locales from document
     *
     * @returns []
     */
    getDocumentLocales() {
        let locales = [];
        const document = this.getDocument();
        if (document && document.translationsSorted && document.translationsSorted instanceof Object) {
            locales = Object.keys(document.translationsSorted);
        }

        return locales;
    }

    /**
     * Return all available locales from document
     *
     * @returns []
     */
    getDocumentEnabledLocales() {
        let locales = [];
        const document = this.getDocument();
        if (document && document.translationsSorted && document.translationsSorted instanceof Object) {
            Object.keys(document.translationsSorted).map(locale => {
                if (document.translationsSorted[`${locale}`].enabled && document.translationsSorted[`${locale}`].enabled === true) {
                    locales.push(locale);
                }
            });
        }

        return locales;
    }


    /**
     * Check if in storage are modified values (needed to alert after copy)
     *
     * @returns {boolean}
     */
    hasModifiedElements() {
        let storage = this.getDocumentStorage();
        if (storage && storage.hasModifiedValues && storage.hasModifiedValues === true){
            return true;
        }

        return false;
    }

    /**
     * Return the contentElement groups
     *
     * @returns []
     */
    getContentGroupElements(type = null) {

        const document = this.getDocument();

        let elements = [];

        if (document && document.contentElementsGroups) {

            if (type !== null) {
                document.contentElementsGroups.map(entry => {
                    if (entry.type && (type.toString() === entry.type.toString())) {
                        elements.push(entry);
                    }
                });
            } else {
                elements = document.contentElementsGroups;
            }
        }

        return elements;
    }

    /**
     * Return the current page object
     *
     * @returns {*}
     */
    getPage() {
        return this.getPageById(this.pageId);
    }

    /**
     * Return a page object by given ID
     *
     * @param pageId
     *
     * @returns {*}
     */
    getPageById(pageId) {
        const pages = this.getPages();
        let page = null;
        pages.map(pageEntry => {
            if (pageEntry.id.toString() == pageId.toString()) {
                page = pageEntry;
            }
        });

        return page;
    }

    /**
     * Return pages collection from document storage, all pages entries
     * are enriched (merged) by pagedata from document specification
     *
     * @returns {Array}
     *
     * @private
     */
    _getStoragePages() {

        const storage = this.getDocumentStorageValues();
        let pages = [];

        if (storage && storage.pages && storage.pages.length > 0) {
            storage.pages.map(pageEntry => {

                let newPage = {
                    deletable: false
                };

                let addPage = false;
                if (pageEntry.ref) {
                    const documentPage = this._getDocumentPageById(pageEntry.ref);
                    if (documentPage) {
                        newPage = {
                            ...documentPage,
                            id: pageEntry.id,
                            deletable: true,
                            ref: pageEntry.ref
                        };
                        addPage = true;
                    }
                } else {
                    const documentPage = this._getDocumentPageById(pageEntry.id);
                    if (documentPage) {
                        newPage = {
                            ...documentPage,
                        };
                        addPage = true;
                    }
                }

                if (addPage === true) {
                    pages.push(newPage);
                }
            });
        }

        return pages;
    }

    /**
     * Return raw, but cleaned pages collection from storage
     *
     * - doesn´t enrich data from referenced page element
     * - pages with broken references will be deleted from this collection
     *
     * @private
     *
     * @returns {Array}
     */
    _getStoredPagesRaw() {
        const storage = this.getDocumentStorageValues();
        let pages = [];

        if (storage && storage.pages && storage.pages.length > 0) {
            storage.pages.map(pageEntry => {
                let add = false;
                if (pageEntry.ref && this._getDocumentPageById(pageEntry.ref))  {
                    add = true;
                } else if (pageEntry.id && this._getDocumentPageById(pageEntry.id)) {
                    add = true;
                }

                if (add) {
                    pages.push(pageEntry);
                }
            });
        }

        return pages;
    }

    /**
     * Returns collection of pages from document specification
     *
     * @returns {Array}
     */
    getDocumentPages() {
        let preparedPages = [];
        const specificaton = this.getDocumentSpecification();
        if (specificaton && specificaton.pages && specificaton.pages.length > 0) {
            specificaton.pages.map(pageEntry => {
                let preparedEntry = pageEntry;
                preparedEntry.deletable = false; // set default deletable flag
                preparedPages.push(preparedEntry);
            });
        }

        return preparedPages;
    }

    /**
     * Return page object from documentSpecification by given page ID
     *
     * @param string pageId
     *
     * @private
     *
     * @returns {*}
     */
    _getDocumentPageById(pageId) {
        let page = null;
        this.getDocumentPages().map(pageEntry => {
            if (pageEntry.id.toString() === pageId.toString()) {
                page = pageEntry;
            }
        });

        return page;
    }

    /**
     * Return the ID of the first page
     *
     * @returns string
     */
    getFirstPageId() {

        let pageId = null;
        const pages = this.getPages();
        if (pages.length > 0) {
            pageId = pages[0] && pages[0].id ? pages[0].id : null;
        }

        return pageId;
    }

    /**
     * Return the current page index, first page starts with 0
     *
     * @returns {number}
     */
    getCurrentPageIndex() {
        let i = 0;
        let currentIndex = 0;
        this.getPages().map(pageEntry => {
            if (pageEntry.id.toString() === this.getPageId().toString()) {
                currentIndex = i;
            }
            i++
        });

        return currentIndex;
    }

    getPageVariant(page) {

        let variant = null;
        let pageId;

        if (page && page.id && page.variants && page.variants instanceof Array && page.variants.length > 0) {

            pageId = page.id;

            let storedVariantId = null;
            const storedPages =  this._getStoredPagesRaw();

            storedPages.map(storedPage => {
                if (storedPage.variantId && storedPage.id.toString() === pageId.toString()) {
                    storedVariantId = storedPage.variantId;
                }
            });

            if (storedVariantId) {
                page.variants.map(pageVariant => {
                    if (pageVariant.id.toString() === storedVariantId.toString()) {
                        variant = pageVariant;
                    }
                });
            }

            if (!variant) {
                variant = page.variants[0];
            }
        }

        return variant;
    }

    getPageVariantById(pageId, variantId) {

        let variant = null;
        const page = this.getPageById(pageId);

        if (page && page.variants && page.variants instanceof Array && page.variants.length > 0) {
            page.variants.map(entry => {
                if (entry.id.toString() === variantId.toString()) {
                    variant = entry;
                }
            })
        }

        return variant;
    }

    getPageVariantLabels(page) {
        let labels = [];
        if (page.variants && page.variants instanceof Array) {
            page.variants.map(entry => {
                labels.push(
                    {
                        "id": entry.id,
                        "title": entry.title,
                    }
                )
            })
        }

        return labels;
    }

    /**
     * Return areas collection by current page
     *
     * @returns {Array}
     */
    getAreas(pageId = null) {

        let areas = [];
        let page;
        if (pageId !== null) {
            page = this.getPageById(pageId)
        } else {
            page = this.getPage();
        }
        const pageVariant = this.getPageVariant(page);

        if (pageVariant && pageVariant.areas && pageVariant.areas instanceof Array && pageVariant.areas.length > 0 ) {
            areas = pageVariant.areas;
        } else if (page && page.areas && page.areas instanceof Array && page.areas.length > 0) {
            areas = page.areas;
        }

        return areas;
    }

    /**
     * Get layout element by given area
     *
     * @param area
     *
     * @returns string
     */
    getLayoutElement(area) {
        let priceFieldOption=this.getDocumentStorage().pricingField;
        console.log('priceFieldOptionX');
        console.log(priceFieldOption);
        if (priceFieldOption!='none' && priceFieldOption!='empty'){

            let layoutElement = null;

            // get template
            if (area.element && area.element.layout && (typeof area.element.layout == 'object') && area.element.layout.configurator) {
                // template is defined in the area section
                layoutElement = area.element.layout.configurator;

            } else if (area.element && area.element.layout && (typeof area.element.layout == 'string') ) {
                // template is referenced by ID of layoutElement
                var layoutElementId = area.element.layout;
                var layoutElementReference = this.getReferencedLayoutElement(layoutElementId);

                if (layoutElementReference && layoutElementReference.layout && layoutElementReference.layout.configurator) {
                    layoutElement = layoutElementReference.layout.configurator;
                } else {
                    console.error('[PLSK] - Can not resolve layoutElement by reference key "' + layoutElementId + '"');
                }
            }
            return layoutElement;
        }else {
            return null;
        }
    }
    /**
     * Returns the layout element by given element ID
     *
     * @param layoutElementId
     *
     * @returns string
     */
    getReferencedLayoutElement(layoutElementId) {
        const document = this.getDocument();
        let layoutElement = null;
        if (document && document.layoutElements && document.layoutElements.length > 0) {
            document.layoutElements.map(function(layoutElementEntry) {
                if (layoutElementEntry.id.toString() == layoutElementId.toString()) {
                    layoutElement = layoutElementEntry;
                }
            });
        }

        return layoutElement;
    }

    /**
     * Return contentElement by given ID
     *
     * @param contentElementId
     *
     * @returns {*}
     */
    getContentElement(contentElementId) {

        const groups = this.getContentGroupElements();

        let contentElement = null;

        if (groups && groups.length > 0) {
            groups.map(function (groupEntry) {
                if (groupEntry && groupEntry.contentElements && groupEntry.contentElements.length > 0) {
                    groupEntry.contentElements.map(function (contentElementEntry) {
                        if (contentElementId.toString() === contentElementEntry.id.toString()) {
                            contentElement = contentElementEntry;
                        }
                    })
                }
            })
        }

        return contentElement;
    }

    /**
     * Return storage values by given areaID
     *
     * @param areaId
     *
     * @returns {Array}
     */
    getAreaStorage(areaId, pageId = null) {

        if (pageId === null) {
            pageId = this.getPageId();
        }
        const storageValues = this.getDocumentStorageValues();
        let storage = false; // default value!

        if (storageValues &&
            storageValues.data &&
            storageValues.data[`${pageId}`] &&
            storageValues.data[`${pageId}`][`${areaId}`] ) {
            storage = storageValues.data[`${pageId}`][`${areaId}`];
        }

        return storage;
    }

    /**
     *
     * @param areaData
     * @param areaDataId
     * @returns {*}
     */
    getAreaDataById(areaData, areaDataId) {

        let data = null;

        areaData.map(function(dataEntry) {
            if (dataEntry.data && dataEntry.data.id  && dataEntry.data.id.toString() === areaDataId.toString()) {
                data = dataEntry.data;
            }
        });

        return data;
    }

    /**
     * Create and return Drag&Drop elements by given area ID
     *
     * @param areaId
     *
     * @returns {Array}
     */
    getAreaDndElements(areaId, pageId = null)
    {
        const areas = this.getAreas(pageId);
        let area = null;
        areas.map(function(areaEntry) {
            if (areaEntry.id.toString() === areaId.toString()) {
                area = areaEntry;
            }
        });

        var areaStorage = this.getAreaStorage(areaId, pageId);

        var storageLocale = this.getDocumentStorageLocale();

        var dndElements = [];

        // Build collection by document storage
        if (areaStorage !== false) { // if false, there is no storage objects stored -> build by default data
            areaStorage.map(storageEntry => {

                // default dndElement
                var dndElement = {
                    ref: {
                        contentElement: null, // = ID from ContentElement
                        areaData: null,       // = ID, defined in the data section in document description
                    },
                    data: {
                        contentElement: {},   // = data, defined in contentElement - merged with custom specification
                        document: {},         // = date, defined in document description
                        documentStorage: {}   // = data, defined in document storage
                    },
                    createdBy: null
                };

                var addElement = false;

                // map from storageEntry (contentElement reference, ID is referenced)
                if (storageEntry.ref && storageEntry.ref.contentElement) {
                    dndElement.ref.contentElement = storageEntry.ref.contentElement;
                    const contentElement = this.getContentElement(storageEntry.ref.contentElement);
                    if (!contentElement) {
                        console.error('[PLSK] - Can not resolve contentElement in documentStorage, by referenced ID "' + storageEntry.ref.contentElement + '"');
                    } else {
                        const contentElementData = this.getContentElementDataByLocale(contentElement, storageLocale);
                        if (contentElementData) {
                            dndElement.data.contentElement = contentElementData;
                            addElement = true;
                        }
                    }
                }

                // map from storageEntry (areaData reference, ID is referenced)
                if (storageEntry.ref && storageEntry.ref.areaData) {
                    dndElement.ref.areaData = storageEntry.ref.areaData;
                    let areaData = null;
                    if (area.element && area.element.data) {
                        areaData = this.getAreaDataById(area.element.data, storageEntry.ref.areaData);
                    }

                    if (areaData) {
                        dndElement.data.document = areaData;
                        addElement = true;
                    }
                }

                // Map overwritten data
                let storageData = {};
                let storageEnrich = {}; // TODO ... check if we need this one
                if (storageEntry.data) {
                    storageData = storageEntry.data;
                }
                if (storageEntry.enrich) {
                    storageEnrich = storageEntry.enrich;
                }

                let mergedStorageData = {...storageData, ...storageEnrich};

                if (Object.keys(mergedStorageData).length > 0) {
                    dndElement.data.documentStorage = mergedStorageData;
                    addElement = true;
                }

                if (addElement) {
                    dndElements.push(dndElement);
                }

                // Map createdBY
                if (storageEntry.createdBy) {
                    dndElement.createdBy = storageEntry.createdBy;
                }
            });
        }

        // default data from area data - only if storage is empty
        else if (area && area.element && area.element.data && area.element.data.length > 0) {

            area.element.data.map(dataEntry => {

                // dataEntry  = data object aus document specification
                // dndElement = angereichertes Objekt fürs DND

                var dndElement = {
                    ref: {
                        contentElement: null,
                        areaData: null
                    },
                    data: {
                        contentElement: {},
                        document: {},
                        documentStorage: {}
                    },
                    createdBy: null
                };

                var addElement = false;

                if (dataEntry.data) {
                    dndElement.data.document = dataEntry.data;
                    if (dataEntry.data.id) {
                        dndElement.ref.areaData = dataEntry.data.id;
                    }
                    addElement = true;
                }

                if (dataEntry.ref && dataEntry.ref.contentElement) {
                    dndElement.ref.contentElement = dataEntry.ref.contentElement;

                    const contentElement = this.getContentElement(dataEntry.ref.contentElement);
                    if (!contentElement) {
                        console.error('[PLSK] - Can not resolve contentElement in documentSpecification, by referenced ID "' + dataEntry.ref.contentElement + '"');
                    } else {
                        const contentElementData = this.getContentElementDataByLocale(contentElement, storageLocale);
                        if (contentElementData) {
                            dndElement.data.contentElement = contentElementData;
                            addElement = true;
                        }
                    }
                }

                if (addElement) {
                    dndElements.push(dndElement);
                }
            });
        }

        return dndElements;
    }

    /**
     * Update storage data and return the whole document storage object
     *
     * @param areaId
     * @param dndElements
     *
     * @returns {*}
     */
    updateStorageData(areaId, dndElements = []) {


        let areaStorageData = [];

        // maybee intial storage is an empty collection - serverside bug,
        // empty objects stored as empty collections serverside

        let storage = this.getDocumentStorageValues();

        const pageId = this.getPageId();

        if (!storage.data) {
            storage.data = {};
        }

        if (!storage.data[`${pageId}`]) {
            storage.data[`${pageId}`] = {};
        }

        dndElements.map(function (dndElement, i) {

            // default storage element
            let storageElem = {
                ref: {
                    contentElement: null,
                    areaData: null
                },
                data: {
                },
                createdBy: null
            };

            // map contentElement-ID
            if (dndElement.ref && dndElement.ref.contentElement) {
                storageElem.ref.contentElement = dndElement.ref.contentElement;
            }

            // map areaData ID
            if (dndElement.ref && dndElement.ref.areaData) {
                storageElem.ref.areaData = dndElement.ref.areaData;
            }

            // map storageData (= modified Data by USER)
            if (dndElement.data && dndElement.data.documentStorage) {
                storageElem.data = dndElement.data.documentStorage;
            }

            if (dndElement.createdBy) {
                storageElem.createdBy = dndElement.createdBy
            }

            areaStorageData.push(storageElem);
        });

        storage.data[`${pageId}`][`${areaId}`] = areaStorageData;

        return storage;
    }

    /**
     * Update pages in documentStorage an return the whole storage object
     *
     * @param array pages
     *
     * @returns {{}}
     */
    updatePagesInStorage(pages) {
        let storage = this.getDocumentStorageValues();

        // we store only the ID, the referenced page and the variantId
        let storedPages = [];
        pages.map(pageEntry => {
            let storedPage = {
                id: pageEntry.id,
            }

            if (pageEntry.ref) {
                storedPage.ref = pageEntry.ref;
            }

            if (pageEntry.variantId) {
                storedPage.variantId = pageEntry.variantId;
            }

            storedPages.push((storedPage));
        });

        storage.pages = storedPages;

        return storage;
    }

    /**
     * Update uploads in documentStorage an return the whole storage object
     *
     * @param areaId
     * @param uploadWidgetId
     * @param uploadedFileId
     * @param userId
     *
     * @returns {*}
     */
    addUploadToStorage(areaId, uploadWidgetId, uploadedFileId, userId) {

        let storage = this.getDocumentStorageValues();
        const pageId = this.getPageId();

        // build object if empty
        if (!storage.uploads) {
            storage.uploads = {};
        }
        if (!storage.uploads[pageId]) {
            storage.uploads[pageId] = {};
        }
        if (!storage.uploads[pageId][areaId] ||
            (storage.uploads[pageId][areaId] instanceof Array && storage.uploads[pageId][areaId].length === 0)) {
            storage.uploads[pageId][areaId] = {};
        }
        if (!storage.uploads[pageId][areaId][uploadWidgetId]) {
            storage.uploads[pageId][areaId][uploadWidgetId] = {};
        }

        // update storage
        const newStorage = update(storage, { // @see https://facebook.github.io/react/docs/update.html
                uploads: {
                    [pageId]: {
                        [areaId]: {
                            [uploadWidgetId]: {
                                $set: {
                                    id: uploadedFileId,
                                    createdBy: userId
                                }
                            }
                        }
                    }
                }
            }
        );

        return newStorage;
    }

    removeUploadFromStorage(areaId, uploadWidgetId) {

        let storage = this.getDocumentStorageValues();
        const pageId = this.getPageId();

        if (storage && storage.uploads && storage.uploads[`${pageId}`] &&
            storage.uploads[`${pageId}`][`${areaId}`] &&  storage.uploads[`${pageId}`][`${areaId}`][`${uploadWidgetId}`]) {
            delete storage.uploads[`${pageId}`][`${areaId}`][`${uploadWidgetId}`];
        }

        return storage;
    }

    getUploadFromStorage(areaId, uploadWidgetId) {

        const storage = this.getDocumentStorageValues();
        const pageId = this.getPageId();

        let imageUpload = null;

        if (storage && storage.uploads && storage.uploads[`${pageId}`] &&
            storage.uploads[`${pageId}`][`${areaId}`] &&  storage.uploads[`${pageId}`][`${areaId}`][`${uploadWidgetId}`]) {
            imageUpload = storage.uploads[`${pageId}`][`${areaId}`][`${uploadWidgetId}`];
        }

        return imageUpload;
    }

    /**
     * Set documentspecification ID to storage and return the whole document storage object
     *
     * @param specificationId
     *
     * @returns {*}
     */
    setDocumentSpecificationIdToStorage(specificationId) {
        let storage = this.getDocumentStorageValues();
        storage.documentSpecificationId = specificationId;

        return storage;
    }

    /**
     * Return the storage values object
     *
     * @returns {*}
     */
    getDocumentStorage() {

        let storage = null;
        this.documentStorageState.data.map(storageElem => {
            if (storageElem.id.toString() === this.storageId.toString()) {
                storage = storageElem;
            }
        });

        return storage;
    }

    /**
     * Get current locale from storage
     *
     * @returns {string}
     */
    getDocumentStorageLocale() {

        let locale = 'de_DE'; // TODO ... get defaultLocale from config
        let storage = this.getDocumentStorage();

        if (storage && storage.locale) {
            locale = storage.locale;
        }

        return locale;

    }

    getDocumentStorageTitle() {

        const storage = this.getDocumentStorage();

        let title = null;
        if (storage && storage.title) {
            title = storage.title;
        }

        return title;
    }

    getDocumentStorageMemo() {

        const storage = this.getDocumentStorage();

        let memo = null;
        if (storage && storage.memo) {
            memo = storage.memo;
        }

        return memo;
    }

    /**
     * Get document storage values object
     *
     * @returns {{}}
     */
    getDocumentStorageValues() {

        const storage = this.getDocumentStorage();

        let storageValues = {};

        if (storage.documentValues) {
            storageValues = storage.documentValues;
        }

        if ((storageValues instanceof Array && storageValues.length === 0) || storageValues === null) {
            storageValues = {};
        }

        return storageValues;
    }

    /**
     * Return contentElement Data by given locale as key/value object
     *
     * Sample ContentElement JSON
     *
     * {
     *      "type": "beer",
     *      "customSpecification": {
     *         "my-key": "my global value"
     *       },
     *       "id": 1,
     *       "name": "Paulaner Weißbier",
     *       "translationsSorted": {
     *           "de_DE": {
     *               "customSpecificationTranslated": {
     *                   "my-key": "de_DE value"
     *               },
     *               "title": "Paulaner Weißbier DE",
     *               "description": "ein voll gutes Weißbier",
     *               "price": "4.8",
     *               "volume": "0.5"
     *           }
     *       },
     *       "translations": [{
     *           "customSpecificationTranslated": {
     *               "from": "de_DE"
     *           },
     *           "title": "Paulaner Weißbier DE",
     *           "description": "ein voll gutes Weißbier",
     *           "price": "4.8",
     *           "volume": "0.5"
     *       }],
     *       "currentLocale": "de_DE",
     *       "defaultLocale": "de_DE"
     *   }
     *
     * @param contentElement
     * @param locale
     *
     * @returns {{}}
     */
    getContentElementDataByLocale(contentElement, locale) {

        const defaultLocale = 'de_DE'; // TODO ... get defaultLocale from config
        let translatedData = {};
        let baseData = {...contentElement};
        let data = {};

        // add some base data from root node
        if (baseData.translationsSorted)  {
            delete baseData.translationsSorted;
        }


        // add translated data by given locale or default locale
        if (contentElement.translationsSorted && contentElement.translationsSorted[`${locale}`]) {
            translatedData = contentElement.translationsSorted[`${locale}`];
        }
        else if (contentElement.translationsSorted && contentElement.translationsSorted[`${defaultLocale}`]) {
            translatedData = contentElement.translationsSorted[`${defaultLocale}`];
        }

        // custom data can be defined global or in the translation section
        let customGlobal = {};
        if (contentElement.customSpecification) {
            customGlobal = contentElement.customSpecification;
        }

        let customTranslated = {};
        if (translatedData.customSpecificationTranslated) {
            customTranslated = translatedData.customSpecificationTranslated;
            //delete elementData.customSpecificationTranslated;
        }

        // merge DATA
        data = {...baseData, ...translatedData, ...customGlobal, ...customTranslated};

        // prepare images, set it to key value storage
        // key consists of three parts, separated by a dot
        // 1. part: prefixname "image"
        // 2. part: the imageName
        // 3. part: the cachekey
        // the value is the absolute path to the cached image, e.g.
        // image.bottleImage.zc-400x500 = "/path/to/bottle-image.jpg"
        if (data.imagesGenerated) {
            const imageKey1 = 'image';
            Object.keys(data.imagesGenerated).map(imageName => {
                const imageKey2 = imageKey1 + '.' + imageName;
                if (data.imagesGenerated[`${imageName}`]) {
                    Object.keys(data.imagesGenerated[`${imageName}`]).map(cacheKey => {
                        const imageKey3 = imageKey2 + '.' + cacheKey;
                        const imagePath = data.imagesGenerated[`${imageName}`][`${cacheKey}`]
                        data[`${imageKey3}`] = imagePath;
                    });
                }
            });

            delete data.imagesGenerated;
        }

        // set thumb, defined by constant // TODO ... get value by config
        if (data[`${THUMB_IMAGE_KEY}`]) {
            data.thumb = data[`${THUMB_IMAGE_KEY}`];
        }

        return data;
    }

    /**
     * Return current style definitions
     *
     * @returns {{}}
     */
    getDocumentCss() {

        let css = {};

        const specification = this.getDocumentSpecification();

        if (specification && specification.styles && specification.styles.configurator) {
            css = specification.styles.configurator;
        }

        return css;
    }

    /**
     * Check if user can download / print hires PDF directly
     *
     * @returns {boolean}
     */
    isUserPrintable() {
        const document = this.getDocument();
        if (document && document.userPrintable === true) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Check if user can send hires PDF to printshop
     *
     * @returns {boolean}
     */
    isPrintshopPrintable() {
        const document = this.getDocument();
        if (document && document.printshopPrintable === true) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Check if there is a webversion of the PDF available
     *
     * @returns {boolean}
     */
    isWebVersionAvailable() {
        const document = this.getDocument();
        if (document && document.webVersionAvailable === true) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Check if is approval process is required
     *
     * @returns {boolean}
     */
    isApproval() {
        const document = this.getDocument();
        if (document && document.approvalProcess === true) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Check if is approval process is a success
     *
     * @returns {boolean}
     */
    showApprovalSuccess() {
        if (this.configuratorState.showApprovalSuccess === true) {
            return true;
        } else {
            return false;
        }
    }

    getUploadSettings() {
        const specification = this.getDocumentSpecification();
        if (specification && specification.uploadSettings) {
            return specification.uploadSettings
        } else {
            return {}
        }
    }

    getUploadSettingById(id) {
        const uploadSettings = this.getUploadSettings();
        let uploadValue = false;
        Object.keys(uploadSettings).map(settingId => {
            if (settingId.toString() === id.toString()) {
                uploadValue = uploadSettings[`${settingId}`];
            }
        });

        return uploadValue;
    }

    getImageKeys() {
        const document = this.getDocument();
        if (document && document.imageKeys) {
            return document.imageKeys
        } else {
            return []
        }
    }

    getImageKey(key) {
        let imageKey = null;
        this.getImageKeys().map(entry => {
            if (entry.key === key) {
                imageKey = entry;
            }
        })

        return imageKey;
    }

    /**
     * Check if page is addable
     *
     * @returns {boolean}
     */
    isPageAddable() {
        const specification = this.getDocumentSpecification();
        if (specification && specification.pageAddable && specification.pageAddable === true) {
            return true;
        }

        return false;
    }

    /**
     * Check if page is sortable
     *
     * @returns {boolean}
     */
    isPageSortable() {
        const specification = this.getDocumentSpecification();
        if (specification && specification.pageSortable && specification.pageSortable === true) {
            return true;
        }

        return false;
    }

    /**
     * Return inputConstraints by given areaId and fieldName
     * @param areaId
     * @param fieldName
     *
     * @returns {*}
     */
    getInputConstraints(areaId, fieldName) {

        let area = null;
        const areas = this.getAreas();
        areas.map(function(areaEntry) {
            if (areaEntry.id.toString() === areaId.toString()) {
                area = areaEntry;
            }
        });

        console.log('AREA', area);

        if (area !== null && area.element.inputConstraints && area.element.inputConstraints[`${fieldName}`]) {
            return area.element.inputConstraints[`${fieldName}`];
        }

        return null;
    }

    /**
     * Return paper qualities if defined
     *
     * @returns {boolean}
     */
    getPaperQualities() {
        const document = this.getDocument();
        if (document && document.paperQualities) {
            return document.paperQualities;
        } else {
            return [];
        }
    }

    /**
     * Validate tha page if areas filled correctly
     *
     * @param pageId
     * @returns {{success: boolean, errorMessages: Array}}
     */
    validate(pageId) {

        let areaCounts = {};
        let areas = [];
        let validation = null;
        const page = this.getPageById(pageId);
        const pageVariant = this.getPageVariant(page);

        if (pageVariant && pageVariant.areas && pageVariant.areas instanceof Array && pageVariant.areas.length > 0 ) {
            areas = pageVariant.areas;
        } else if (page && page.areas && page.areas instanceof Array && page.areas.length > 0) {
            areas = page.areas;
        }

        areas.map(areaEntry => {
            if (areaEntry.draggable && areaEntry.draggable === true) {
                areaCounts[`${areaEntry.id}`] = this.getAreaDndElements(areaEntry.id, pageId).length;
            }
        });

        if (pageVariant && pageVariant.validation) {
            validation = pageVariant.validation;
        } else if (page && page.validation) {
            validation = page.validation;
        }

        // console.log('A11 pageId', pageId);
        // console.log('A11 areas', areas);
        // console.log('A11 areaCounts', areaCounts);
        // console.log('A11 validation', validation);


        const comparators = {
            "==": (a, b) => a === b,
            "!=": (a, b) => a !== b,
            ">=": (a, b) => a >= b,
            ">": (a, b) => a > b,
            "<=": (a, b) => a <= b,
            "<": (a, b) => a < b,
        };

        let errorMessages = [];
        if (validation && validation.areaElementCount && validation.areaElementCount instanceof Array) {

            // loop over all validation entries
            validation.areaElementCount.forEach(v => {
                //console.log('A11 - v', v);
                //console.log('A11 -----------------------------------------------');

                // each validation entry has an errorIf collection

                let level1Match = null;

                _.get(v, 'errorIf', []).forEach(e1 => {

                    // the first level is ANDed
                    //console.log('A11 e1', e1);

                    let level2Match = false;

                    e1.forEach(e2 => {

                        // mutliple entries in second level are ORed
                        const areaId = _.get(e2, 'area', null);
                        const operator = _.get(e2, 'compare', null);
                        const compareValue = _.get(e2, 'value', null);

                        //console.log('A11 e2', e2);
                        //console.log('A11 e2,value (compareValue)', compareValue);

                        if (!areaId) {
                            console.warn('[PLSK - validation.areaElementCount] - Each entry in the errorIf collection requires an "area" field');
                        }
                        if (!operator) {
                            console.warn('[PLSK - validation.areaElementCount] - Each entry in the errorIf collection requires a "compare" field');
                        }
                        if (compareValue === null) {
                            console.warn('[PLSK - validation.areaElementCount] - Each entry in the errorIf collection requires a "value" field');
                        }

                        const comparator = comparators[`${operator}`];
                        if (!comparator) {
                            console.warn(`[PLSK - validation.areaElementCount] - Invalid compare field "${operator}" in the errorIf collection entry`);
                        }

                        const elementCountValue = _.get(areaCounts, areaId, 0);
                        if (areaId && operator && compareValue !== null && comparator) {
                            //console.log('A11 - comparator(elementCountValue, compareValue)', comparator, elementCountValue, compareValue);
                            if (comparator(elementCountValue, compareValue)) {
                                //console.log('A11 - comparator success, break loop');
                                level2Match = true;
                            }
                        }
                    });

                    if (level2Match && level1Match !== false) {
                        //console.log('A11 - level 1  matched');
                        level1Match = true;
                    } else {
                        level1Match = false;
                        //console.log('A11 - level 1 not matched, break loop')
                    }
                });

                // add error message!
                if (level1Match) {
                    errorMessages.push(v.errorMessage);
                }
            });
        }

        return {success: !(errorMessages.length > 0), errorMessages: errorMessages };
    }
}

export default ConfiguratorManager;



// WEBPACK FOOTER //
// ./src/js/ConfiguratorManager.js